import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { analisesAPI, aprovacoesAPI, comparativaAPI, analiseComentariosAPI } from '../../services/inovacaoAPI';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Send, CheckCircle, XCircle, Archive, RotateCcw, UserPlus, ClipboardCheck, Save } from 'lucide-react';
import ModalCampos from './ModalCampos';
import { rotuloEstadoAnalise, mensagemErro } from './labels';

const BADGE = (cor) => ({ background: cor, color: '#fff', padding: '4px 12px', borderRadius: 12, fontSize: '0.85rem' });
const BTN = { padding: '8px 16px', borderRadius: 4, border: 'none', cursor: 'pointer', marginRight: 8, marginBottom: 8 };
const ctaBar = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                 flexWrap: 'wrap', padding: '14px 18px', borderRadius: 10, marginBottom: 16,
                 background: '#eafaf1', border: '1px solid #b9eccb' };
const ctaBtn = { background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', padding: '10px 18px', borderRadius: 8,
                 textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' };
const SUB = { color: '#7f8c8d', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4,
              margin: '12px 0 2px' };

function SecaoTitulo({ children }) {
  return (
    <h3 style={{ margin: '0 0 6px', color: '#2c3e50', fontSize: '1.15rem',
                 borderLeft: '4px solid var(--brand, #20a490)', paddingLeft: 10 }}>{children}</h3>
  );
}

// Stepper macro da jornada: Análise → Aprovação → Projeto
function StepperMacro({ estado }) {
  if (['rejeitada', 'backlog', 'cancelada'].includes(estado)) return null;
  const passos = ['Análise', 'Aprovação', 'Projeto'];
  const doneCount = estado === 'projeto_criado' ? 3
    : estado === 'aprovada' ? 2
    : estado === 'em_aprovacao' ? 1 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      {passos.map((p, i) => {
        const done = i < doneCount;
        const ativo = i === doneCount && doneCount < 3;
        const cor = done ? '#27ae60' : ativo ? 'var(--brand, #20a490)' : '#cfd8dc';
        return (
          <React.Fragment key={p}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: cor, color: '#fff',
                             display: 'flex', alignItems: 'center', justifyContent: 'center',
                             fontSize: '0.72rem', fontWeight: 700 }}>{done ? '✓' : i + 1}</span>
              <span style={{ fontSize: '0.82rem', color: (done || ativo) ? '#2c3e50' : '#95a5a6',
                             fontWeight: ativo ? 600 : 400 }}>{p}</span>
            </div>
            {i < passos.length - 1 && <span style={{ width: 28, height: 2, background: '#e0e6e8' }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function AnaliseDetalhe() {
  const { id } = useParams();
  const { user } = useAuth();
  const [analise, setAnalise] = useState(null);
  const [aprovadores, setAprovadores] = useState([]);
  const [aprovacoes, setAprovacoes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [aba, setAba] = useState('visao');
  const [modalRejeitar, setModalRejeitar] = useState(false);

  async function recarregar() {
    const a = await analisesAPI.obter(id);
    setAnalise(a);
    setAprovadores(await aprovacoesAPI.listarAprovadores(id).catch(() => []));
    setAprovacoes(await aprovacoesAPI.listarAprovacoes(id).catch(() => []));
  }

  useEffect(() => {
    recarregar().catch((e) => toast.error(mensagemErro(e, 'Erro')));
    authAPI.listUsers().then((r) => setUsuarios(r.data)).catch(() => {});
  }, [id]);

  if (!analise) return <div style={{ padding: 32 }}>Carregando…</div>;

  async function acao(fn, msgSucesso) {
    try { await fn(); toast.success(msgSucesso); await recarregar(); }
    catch (e) { toast.error(mensagemErro(e, 'Erro')); }
  }

  // Papel de quem abriu a MESMA tela: operador (respondente designado) vs gestor.
  const souOperador = !!user && analise.responsavel_respostas?.id === user.id;
  const souAdmin = user?.perfil === 'administrador';
  // Gestor = quem gerencia (não é só o operador). Admin sempre gerencia.
  const podeGerir = !souOperador || souAdmin;
  const operadorPodeResponder = souOperador && analise.estado === 'aguardando_respostas';

  const podeConvidar = podeGerir && analise.estado === 'rascunho';
  const podeEnviarAprovacao = podeGerir && analise.estado === 'em_revisao_gni' && aprovadores.length > 0;
  const podeRejeitar = podeGerir && analise.estado === 'em_revisao_gni';
  const podeBacklog = podeGerir && ['em_revisao_gni', 'rejeitada'].includes(analise.estado);
  const podeReabrir = podeGerir && ['rejeitada', 'backlog'].includes(analise.estado);

  return (
    <div style={{ padding: 32 }}>
      <Link to="/inovacao/analises" style={{ color: 'var(--brand, #20a490)' }}>← Análises</Link>
      <h1 style={{ marginTop: 8 }}>{analise.titulo}</h1>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <span style={BADGE(rotuloEstadoAnalise(analise.estado).cor)}>{rotuloEstadoAnalise(analise.estado).label}</span>
        <span style={{ color: '#777' }}>
          Setor: <b>{analise.setor_solicitante?.nome}</b> ·
          Solicitante: <b>{analise.responsavel_respostas?.nome}</b> ·
          Executor: <b>{analise.executor_gni?.nome}</b>
        </span>
        {analise.score_oportunidade != null && (
          <span style={{ background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', padding: '4px 12px', borderRadius: 6 }}>
            Score: <b>{analise.score_oportunidade}</b>
          </span>
        )}
      </div>

      <StepperMacro estado={analise.estado} />

      <div style={{ marginBottom: 16 }}>
        {podeConvidar && (
          <button style={{ ...BTN, background: '#3498db', color: '#fff' }}
                  onClick={() => acao(() => analisesAPI.convidar(id, {}), 'Convite enviado')}>
            <Send size={14} /> Convidar respondente
          </button>
        )}
        {podeGerir && analise.estado === 'aguardando_respostas' && (
          <button style={{ ...BTN, background: '#3498db', color: '#fff' }}
                  onClick={() => acao(() => analisesAPI.reenviarConvite(id), 'Convite reenviado')}>
            <Send size={14} /> Reenviar convite
          </button>
        )}
        {podeEnviarAprovacao && (
          <button style={{ ...BTN, background: '#f39c12', color: '#fff' }}
                  onClick={() => acao(() => aprovacoesAPI.enviarAprovacao(id), 'Aprovação iniciada')}>
            <CheckCircle size={14} /> Enviar para aprovação
          </button>
        )}
        {podeRejeitar && (
          <button style={{ ...BTN, background: '#e74c3c', color: '#fff' }}
                  onClick={() => setModalRejeitar(true)}>
            <XCircle size={14} /> Rejeitar
          </button>
        )}
        {podeBacklog && (
          <button style={{ ...BTN, background: '#7f8c8d', color: '#fff' }}
                  onClick={() => acao(() => analisesAPI.moverBacklog(id, 'Adiado'), 'Movida para backlog')}>
            <Archive size={14} /> Backlog
          </button>
        )}
        {podeReabrir && (
          <button style={{ ...BTN, background: '#9b59b6', color: '#fff' }}
                  onClick={() => acao(() => analisesAPI.reabrir(id), 'Reaberta')}>
            <RotateCcw size={14} /> Reabrir
          </button>
        )}
      </div>

      {analise.estado === 'aguardando_respostas' && (
        <div style={{ background: '#fff8ef', border: '1px solid #f0c089', borderRadius: 10,
                      padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#b9772a',
                        fontWeight: 600, marginBottom: 6 }}>
            <ClipboardCheck size={18} />
            {souOperador ? 'Você tem um questionário para responder' : 'Aguardando o operador responder'}
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.86rem', color: '#8a6d3b' }}>
            {souOperador
              ? 'Preencha o questionário de esforço da tarefa manual e envie. Suas respostas alimentam a análise da oportunidade.'
              : `O questionário foi enviado a ${analise.responsavel_respostas?.nome || 'o operador'}. Assim que ele responder, você revisa aqui e completa o comparativo.`}
          </p>
          {souOperador && (
            <button style={{ ...BTN, marginBottom: 0, background: '#f39c12', color: '#fff' }}
                    onClick={() => setAba('visao')}>
              Responder questionário
            </button>
          )}
        </div>
      )}

      {analise.estado === 'em_revisao_gni' && podeGerir && (
        <div style={{ background: '#eef6fc', border: '1px solid #bcdcf2', borderRadius: 10,
                      padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1c5d99',
                        fontWeight: 600, marginBottom: 6 }}>
            <ClipboardCheck size={18} /> O operador respondeu, falta a sua revisão
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.86rem', color: '#5b7c95' }}>
            Revise as respostas do operador e informe quanto tempo o sistema novo vai levar. Depois defina os aprovadores e envie para aprovação.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button style={{ ...BTN, marginBottom: 0, background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)' }}
                    onClick={() => setAba('visao')}>
              Ver respostas do operador
            </button>
            <button style={{ ...BTN, marginBottom: 0, background: '#eef1f3', color: '#555' }}
                    onClick={() => setAba('comparativa')}>
              Completar comparativo
            </button>
            <button style={{ ...BTN, marginBottom: 0, background: aprovadores.length === 0 ? '#f39c12' : '#eef1f3',
                             color: aprovadores.length === 0 ? '#fff' : '#555' }}
                    onClick={() => setAba('aprovadores')}>
              {aprovadores.length === 0 ? 'Definir aprovadores (nenhum ainda)' : `Aprovadores (${aprovadores.length})`}
            </button>
          </div>
        </div>
      )}

      {analise.estado === 'aprovada' && !analise.projeto_id && (
        <div style={ctaBar}>
          <span style={{ color: '#1e7e45', fontWeight: 500 }}>
            ✓ Análise aprovada. Próximo passo: transformar em projeto.
          </span>
          <Link to={`/inovacao/projetos/novo?analise=${id}`} style={ctaBtn}>Criar projeto →</Link>
        </div>
      )}
      {analise.projeto_id && (
        <div style={{ ...ctaBar, background: '#eef6ff', border: '1px solid #b3d4fc' }}>
          <span style={{ color: '#1c5d99', fontWeight: 500 }}>Esta análise já virou projeto.</span>
          <Link to={`/inovacao/projetos/${analise.projeto_id}`}
                style={{ ...ctaBtn, background: '#3498db' }}>Abrir projeto →</Link>
        </div>
      )}

      {modalRejeitar && (
        <ModalCampos
          titulo="Rejeitar análise"
          descricao={analise.titulo}
          campos={[{ nome: 'motivo', label: 'Motivo da rejeição', tipo: 'textarea', required: true }]}
          confirmLabel="Rejeitar" confirmCor="#e74c3c"
          onConfirm={(vals) => { setModalRejeitar(false); acao(() => analisesAPI.rejeitar(id, vals.motivo), 'Análise rejeitada'); }}
          onCancel={() => setModalRejeitar(false)}
        />
      )}

      <div style={{ display: 'flex', gap: 16, marginBottom: 16, borderBottom: '2px solid #eee' }}>
        {['visao', 'comparativa', 'aprovadores', 'historico'].map((k) => (
          <button key={k}
                  onClick={() => setAba(k)}
                  style={{
                    padding: '10px 16px', border: 'none', background: 'none',
                    borderBottom: aba === k ? '3px solid var(--brand, #20a490)' : '3px solid transparent',
                    color: aba === k ? 'var(--brand, #20a490)' : '#777', fontWeight: 600, cursor: 'pointer'
                  }}>
            {k === 'visao' ? 'Análise'
             : k === 'comparativa' ? 'Análise Comparativa'
             : k === 'aprovadores' ? 'Aprovadores' : 'Histórico'}
          </button>
        ))}
      </div>

      {aba === 'visao' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 1. Contexto da oportunidade */}
          <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
            <SecaoTitulo>Contexto</SecaoTitulo>
            <h4 style={SUB}>Resumo executivo</h4>
            <p style={{ whiteSpace: 'pre-wrap', margin: '0 0 8px' }}>{analise.resumo_executivo || <em style={{ color: '#aaa' }}>não preenchido</em>}</p>
            <h4 style={SUB}>Objetivo</h4>
            <p style={{ whiteSpace: 'pre-wrap', margin: '0 0 8px' }}>{analise.objetivo || <em style={{ color: '#aaa' }}>não preenchido</em>}</p>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginTop: 4 }}>
              <div>
                <h4 style={SUB}>Setores impactados</h4>
                <p style={{ margin: 0 }}>{analise.setores_impactados?.map((s) => s.nome).join(', ') || '—'}</p>
              </div>
              {analise.horas_mensais_estimadas_economia && (
                <div>
                  <h4 style={SUB}>Horas/mês estimadas</h4>
                  <p style={{ margin: 0 }}>{analise.horas_mensais_estimadas_economia}</p>
                </div>
              )}
            </div>
            <h4 style={SUB}>Observações internas (GNI)</h4>
            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{analise.observacoes_internas || <em style={{ color: '#aaa' }}>—</em>}</p>
          </div>

          {/* 2. Questionário de esforço (operador responde; todos veem) */}
          <QuestionarioEsforcoAba
            analiseId={id}
            editavel={operadorPodeResponder}
            souOperador={souOperador}
            onSubmetido={recarregar}
          />

          {/* 3. Conversa no fim da página */}
          <DiscussaoThread
            analiseId={id}
            usuarios={usuarios}
            congelada={['aprovada', 'projeto_criado', 'cancelada'].includes(analise.estado)}
            meuId={user?.id}
          />
        </div>
      )}

      {aba === 'comparativa' && (
        <ComparativaAba
          analiseId={id}
          podeEditar={podeGerir && !['em_aprovacao', 'aprovada', 'projeto_criado', 'cancelada'].includes(analise.estado)}
        />
      )}

      {aba === 'aprovadores' && (
        <AprovadoresAba
          analiseId={id}
          aprovadores={aprovadores}
          aprovacoes={aprovacoes}
          usuarios={usuarios}
          podeEditar={podeGerir && !['em_aprovacao', 'aprovada', 'projeto_criado', 'cancelada'].includes(analise.estado)}
          recarregar={recarregar}
        />
      )}

      {aba === 'historico' && <HistoricoAba analiseId={id} />}
    </div>
  );
}

function RespostasOperador({ analiseId }) {
  const [r, setR] = useState(undefined); // undefined=carregando, null=sem resposta

  useEffect(() => {
    comparativaAPI.respostasOperador(analiseId).then(setR).catch(() => setR(null));
  }, [analiseId]);

  if (r === undefined) return null;
  if (r === null) {
    return (
      <div style={{ background: '#fff8ef', border: '1px solid #f0c089', borderRadius: 8, padding: '12px 16px', marginBottom: 18, color: '#b9772a', fontSize: '0.88rem' }}>
        O operador ainda não respondeu o questionário de esforço. As respostas aparecem aqui quando ele enviar.
      </div>
    );
  }

  const itens = [
    ['Frequência da tarefa', r.frequencia_verificacao],
    ['Tempo por vez', r.tempo_por_verificacao_minutos != null ? `${r.tempo_por_verificacao_minutos} min` : null],
    ['Impacto em outras tarefas (1-5)', r.impacto_outras_tarefas],
    ['Concentração exigida (1-5)', r.carga_cognitiva],
    ['Estresse / fadiga', r.estresse],
    ['Erros e consequências', r.riscos_tarefa_manual],
    ['Outras dificuldades', r.outras_dificuldades],
    ['Envolve outros setores', r.envolve_outros_setores],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <div style={{ background: '#eef6fc', border: '1px solid #bcdcf2', borderRadius: 8, padding: '14px 18px', marginBottom: 20 }}>
      <h4 style={{ margin: '0 0 4px', color: '#2c3e50' }}>Respostas do operador: esforço da tarefa manual</h4>
      <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: '#5b7c95' }}>
        Preenchido pelo operador. Use isto para completar o lado automático (quanto tempo o sistema novo vai levar).
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        {itens.map(([label, valor]) => (
          <div key={label}>
            <div style={{ fontSize: '0.7rem', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
            <div style={{ fontSize: '0.88rem', color: '#2c3e50', whiteSpace: 'pre-wrap' }}>{String(valor)}</div>
          </div>
        ))}
        {itens.length === 0 && <span style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>Questionário enviado, mas sem campos preenchidos.</span>}
      </div>
    </div>
  );
}

const Q_VAZIO = {
  frequencia_verificacao: '',
  tempo_por_verificacao_minutos: '',
  impacto_outras_tarefas: 3,
  carga_cognitiva: 3,
  estresse: '',
  riscos_tarefa_manual: '',
  impacto_rotina: '',
  outras_dificuldades: '',
  envolve_outros_setores: '',
  observacao_adicional: '',
};

function EscalaEsforco({ valor, editavel, onChange }) {
  const rotulos = { 1: 'Mínimo', 2: 'Baixo', 3: 'Moderado', 4: 'Alto', 5: 'Extremo' };
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!editavel} onClick={() => onChange(n)}
                style={{ width: 40, height: 40, borderRadius: 20,
                         border: `2px solid ${valor === n ? 'var(--brand, #20a490)' : '#ccc'}`,
                         background: valor === n ? 'var(--brand, #20a490)' : '#fff',
                         color: valor === n ? '#fff' : '#555', fontWeight: 700,
                         cursor: editavel ? 'pointer' : 'default' }}>
          {n}
        </button>
      ))}
      <span style={{ marginLeft: 6, color: '#777', fontSize: '0.85rem' }}>{rotulos[valor]}</span>
    </div>
  );
}

function QuestionarioEsforcoAba({ analiseId, editavel, souOperador, onSubmetido }) {
  const [form, setForm] = useState(Q_VAZIO);
  const [carregado, setCarregado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [temResposta, setTemResposta] = useState(false);

  useEffect(() => {
    analisesAPI.respostasEsforco(analiseId)
      .then((r) => {
        if (r) {
          setTemResposta(true);
          setForm({
            frequencia_verificacao: r.frequencia_verificacao || '',
            tempo_por_verificacao_minutos: r.tempo_por_verificacao_minutos || '',
            impacto_outras_tarefas: r.impacto_outras_tarefas || 3,
            carga_cognitiva: r.carga_cognitiva || 3,
            estresse: r.estresse || '',
            riscos_tarefa_manual: r.riscos_tarefa_manual || '',
            impacto_rotina: r.impacto_rotina || '',
            outras_dificuldades: r.outras_dificuldades || '',
            envolve_outros_setores: r.envolve_outros_setores || '',
            observacao_adicional: r.observacao_adicional || '',
          });
        }
      })
      .catch(() => {})
      .finally(() => setCarregado(true));
  }, [analiseId]);

  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  async function salvarRascunho() {
    setSalvando(true);
    try {
      await analisesAPI.salvarRascunhoEsforco(analiseId, form);
      toast.success('Rascunho salvo');
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao salvar')); }
    finally { setSalvando(false); }
  }

  async function submeter() {
    const obrig = ['frequencia_verificacao', 'tempo_por_verificacao_minutos', 'riscos_tarefa_manual', 'impacto_rotina'];
    for (const c of obrig) {
      const v = form[c];
      if (v === '' || v === null || v === undefined || (typeof v === 'string' && !v.trim())) {
        toast.error('Preencha os campos obrigatórios marcados com *'); return;
      }
    }
    setSalvando(true);
    try {
      const atualizada = await analisesAPI.submeterEsforco(analiseId, form);
      toast.success(`Enviado! Score calculado: ${atualizada.score_oportunidade ?? '—'}`);
      onSubmetido && onSubmetido();
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao enviar')); }
    finally { setSalvando(false); }
  }

  if (!carregado) return <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>Carregando…</div>;

  const ro = !editavel;

  return (
    <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
      <SecaoTitulo>Análise de esforço na tarefa manual</SecaoTitulo>
      <p style={{ color: '#7f8c8d', fontSize: '0.85rem', margin: '0 0 16px' }}>
        Respondido pelo operador. Alimenta o cálculo da oportunidade e o comparativo.
      </p>
      {ro && (
        <div style={{ background: temResposta ? '#eef6fc' : '#fff8ef',
                      border: `1px solid ${temResposta ? '#bcdcf2' : '#f0c089'}`,
                      borderRadius: 8, padding: '10px 14px', marginBottom: 18,
                      color: temResposta ? '#1c5d99' : '#b9772a', fontSize: '0.86rem' }}>
          {temResposta
            ? (souOperador
                ? 'Você já enviou o questionário. Estas são as suas respostas (somente leitura).'
                : 'Respostas enviadas pelo operador. Use-as para completar o comparativo na aba Análise Comparativa.')
            : (souOperador
                ? 'O questionário ainda não está aberto para resposta neste estado da análise.'
                : 'O operador ainda não respondeu. As respostas aparecem aqui quando ele enviar.')}
        </div>
      )}

      <QCampo n="1" titulo="Frequência" obrig editavel={editavel}>
        Quantas vezes esse processo manual é realizado?
        <input style={QST.input} value={form.frequencia_verificacao} readOnly={ro}
               placeholder="Ex: 4 vezes por mês, 8 horas por dia…" onChange={set('frequencia_verificacao')} />
      </QCampo>

      <QCampo n="2" titulo="Esforço (minutos por execução)" obrig editavel={editavel}>
        Em média, quantos minutos por execução (busca, agrupamento, conferência)?
        <input type="number" step="0.1" min="0.1" style={QST.input} value={form.tempo_por_verificacao_minutos}
               readOnly={ro} placeholder="Ex: 180 (= 3h)" onChange={set('tempo_por_verificacao_minutos')} />
      </QCampo>

      <QCampo n="3" titulo="Impacto em outras tarefas" obrig editavel={editavel}>
        De 1 (nenhum) a 5 (total): quanto essa atividade manual afeta outras tarefas da equipe?
        <EscalaEsforco valor={form.impacto_outras_tarefas} editavel={editavel}
                       onChange={(v) => setForm({ ...form, impacto_outras_tarefas: v })} />
      </QCampo>

      <QCampo n="4" titulo="Nível de concentração" obrig editavel={editavel}>
        De 1 (baixo) a 5 (extremo): qual o nível de concentração para evitar erros?
        <EscalaEsforco valor={form.carga_cognitiva} editavel={editavel}
                       onChange={(v) => setForm({ ...form, carga_cognitiva: v })} />
      </QCampo>

      <QCampo n="5" titulo="Estresse / fadiga" editavel={editavel}>
        Essa atividade causa estresse, ansiedade ou fadiga?
        <textarea style={{ ...QST.input, minHeight: 60 }} value={form.estresse} readOnly={ro}
                  onChange={set('estresse')} />
      </QCampo>

      <QCampo n="6" titulo="Ocorrência de erros" obrig editavel={editavel}>
        Já ocorreram erros manuais? Quais as consequências?
        <textarea style={{ ...QST.input, minHeight: 80 }} value={form.riscos_tarefa_manual} readOnly={ro}
                  onChange={set('riscos_tarefa_manual')} />
      </QCampo>

      <QCampo n="7" titulo="Impacto na rotina" obrig editavel={editavel}>
        Como essa tarefa impacta a rotina da equipe?
        <textarea style={{ ...QST.input, minHeight: 60 }} value={form.impacto_rotina} readOnly={ro}
                  onChange={set('impacto_rotina')} />
      </QCampo>

      <QCampo n="8" titulo="Outras dificuldades" editavel={editavel}>
        Alguma outra dificuldade ou ponto de dor não mencionado?
        <textarea style={{ ...QST.input, minHeight: 60 }} value={form.outras_dificuldades} readOnly={ro}
                  onChange={set('outras_dificuldades')} />
      </QCampo>

      <QCampo n="9" titulo="Envolve outros setores?" editavel={editavel}>
        Esta atividade depende ou envolve outros setores? Quais?
        <input style={QST.input} value={form.envolve_outros_setores} readOnly={ro}
               placeholder="Ex: cadastro, reembolso, jurídico" onChange={set('envolve_outros_setores')} />
      </QCampo>

      {editavel && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      borderTop: '1px solid #ecf0f1', marginTop: 20, paddingTop: 16 }}>
          <button onClick={salvarRascunho} disabled={salvando}
                  style={{ ...BTN, marginBottom: 0, background: '#fff', color: '#555', border: '1px solid #ddd' }}>
            <Save size={14} /> Salvar rascunho
          </button>
          <button onClick={submeter} disabled={salvando}
                  style={{ ...BTN, marginBottom: 0, background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)' }}>
            <Send size={14} /> Enviar respostas
          </button>
        </div>
      )}
    </div>
  );
}

function QCampo({ n, titulo, obrig, editavel, children }) {
  return (
    <div style={{ marginBottom: 20, padding: 14, background: '#f8f9fa', borderRadius: 6,
                  borderLeft: '3px solid var(--brand, #20a490)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', width: 30, height: 30, borderRadius: 15,
                       display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                       fontWeight: 700, fontSize: '0.8rem', flexShrink: 0 }}>{n}</span>
        <h4 style={{ margin: 0, color: '#2c3e50', fontSize: '0.98rem' }}>
          {titulo} {obrig && editavel && <span style={{ color: '#e74c3c' }}>*</span>}
        </h4>
      </div>
      <div style={{ marginLeft: 40, color: '#555', fontSize: '0.9rem' }}>{children}</div>
    </div>
  );
}

const QST = {
  input: {
    width: '100%', padding: 9, marginTop: 8, borderRadius: 4, border: '1px solid #ccc',
    fontSize: '0.9rem', fontFamily: 'inherit', background: '#fff',
  },
};

function nomeUsuario(u) {
  return u?.nome_completo || u?.nome || u?.email || 'Usuário';
}

function TextoComMencoes({ texto }) {
  // Realça @tokens (nome citado) em negrito verde.
  const partes = String(texto).split(/(@[\wÀ-ÿ.]+)/g);
  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {partes.map((p, i) =>
        p.startsWith('@')
          ? <b key={i} style={{ color: 'var(--brand, #20a490)' }}>{p}</b>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </span>
  );
}

function DiscussaoThread({ analiseId, usuarios, congelada, meuId }) {
  const [coms, setComs] = useState([]);
  const [carregado, setCarregado] = useState(false);
  const [texto, setTexto] = useState('');
  const [mencionados, setMencionados] = useState({}); // id -> nome inserido
  const [sugestoes, setSugestoes] = useState(null);    // {termo, lista} | null
  const [enviando, setEnviando] = useState(false);

  const recarregar = () =>
    analiseComentariosAPI.listar(analiseId).then(setComs).catch(() => setComs([]));

  useEffect(() => {
    recarregar().finally(() => setCarregado(true));
  }, [analiseId]);

  function onChange(e) {
    const v = e.target.value;
    setTexto(v);
    const m = v.slice(0, e.target.selectionStart).match(/@([\wÀ-ÿ.]*)$/);
    if (m) {
      const termo = m[1].toLowerCase();
      const lista = (usuarios || [])
        .filter((u) => nomeUsuario(u).toLowerCase().includes(termo) || (u.email || '').toLowerCase().includes(termo))
        .slice(0, 6);
      setSugestoes({ termo: m[1], lista });
    } else {
      setSugestoes(null);
    }
  }

  function escolherMencao(u) {
    const nome = nomeUsuario(u).split(' ')[0]; // primeiro nome, sem espaço (token simples)
    setTexto((t) => t.replace(/@([\wÀ-ÿ.]*)$/, `@${nome} `));
    setMencionados((mm) => ({ ...mm, [u.id]: nome }));
    setSugestoes(null);
  }

  async function enviar() {
    if (!texto.trim()) return;
    // Só manda como mencionado quem ainda aparece no texto.
    const ids = Object.entries(mencionados)
      .filter(([, nome]) => texto.includes(`@${nome}`))
      .map(([id]) => Number(id));
    setEnviando(true);
    try {
      await analiseComentariosAPI.criar(analiseId, texto.trim(), ids);
      setTexto(''); setMencionados({}); setSugestoes(null);
      await recarregar();
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao comentar')); }
    finally { setEnviando(false); }
  }

  async function remover(cid) {
    try { await analiseComentariosAPI.remover(cid); await recarregar(); }
    catch (e) { toast.error(mensagemErro(e, 'Erro ao remover')); }
  }

  return (
    <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
      <SecaoTitulo>Conversa</SecaoTitulo>
      <p style={{ color: '#7f8c8d', fontSize: '0.85rem', margin: '0 0 16px' }}>
        Dúvidas e alinhamentos sobre a análise. Use <b>@</b> para citar alguém; a pessoa recebe um alerta.
      </p>

      {carregado && coms.length === 0 && (
        <p style={{ color: '#bdc3c7', fontSize: '0.9rem', margin: '0 0 16px' }}>
          Nenhum comentário ainda.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: congelada ? 0 : 20 }}>
        {coms.map((c) => (
          <div key={c.id} style={{ borderLeft: '3px solid #ecf0f1', paddingLeft: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.9rem' }}>
                {c.autor?.id === meuId ? 'Você' : nomeUsuario(c.autor)}
              </span>
              <span style={{ color: '#b0b8bf', fontSize: '0.75rem' }}>
                {new Date(c.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                {c.editado_em && ' · editado'}
              </span>
              {c.pode_editar && !congelada && (
                <button onClick={() => remover(c.id)}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c0392b',
                                 cursor: 'pointer', fontSize: '0.75rem' }}>
                  remover
                </button>
              )}
            </div>
            <div style={{ color: '#34495e', fontSize: '0.9rem' }}>
              <TextoComMencoes texto={c.texto} />
            </div>
          </div>
        ))}
      </div>

      {congelada ? (
        <div style={{ background: '#f4f6f7', borderRadius: 6, padding: '10px 14px',
                      color: '#7f8c8d', fontSize: '0.85rem' }}>
          Análise encerrada: a conversa virou histórico e não aceita novos comentários.
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <textarea value={texto} onChange={onChange}
                    placeholder="Escreva um comentário… use @ para citar"
                    style={{ width: '100%', minHeight: 70, padding: 10, borderRadius: 6,
                             border: '1px solid #dce1e4', fontFamily: 'inherit', fontSize: '0.9rem' }} />
          {sugestoes && sugestoes.lista.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 20, background: '#fff', border: '1px solid #dce1e4',
                          borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 220, marginTop: -6 }}>
              {sugestoes.lista.map((u) => (
                <div key={u.id} onClick={() => escolherMencao(u)}
                     style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.88rem', color: '#2c3e50' }}
                     onMouseEnter={(e) => (e.currentTarget.style.background = '#f4f6f7')}
                     onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
                  {nomeUsuario(u)}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={enviar} disabled={enviando || !texto.trim()}
                    style={{ ...BTN, marginBottom: 0, background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)',
                             opacity: enviando || !texto.trim() ? 0.6 : 1 }}>
              <Send size={14} /> Comentar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ComparativaAba({ analiseId, podeEditar }) {
  const [linhas, setLinhas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    comparativaAPI.listar(analiseId)
      .then((dados) => setLinhas(dados.map((d, i) => ({
        caracteristica: d.caracteristica,
        processo_manual: d.processo_manual || '',
        processo_automatico: d.processo_automatico || '',
        ordem: d.ordem ?? i,
      }))))
      .catch(() => toast.error('Erro ao carregar comparativa'))
      .finally(() => setLoading(false));
  }, [analiseId]);

  async function salvar() {
    try {
      const atualizado = await comparativaAPI.substituir(analiseId, linhas);
      setLinhas(atualizado);
      toast.success('Comparativa salva');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Erro');
    }
  }

  function atualizar(i, campo, valor) {
    const copy = [...linhas];
    copy[i] = { ...copy[i], [campo]: valor };
    setLinhas(copy);
  }

  function adicionar() {
    setLinhas([...linhas, { caracteristica: '', processo_manual: '', processo_automatico: '', ordem: linhas.length }]);
  }

  function remover(i) {
    setLinhas(linhas.filter((_, j) => j !== i));
  }

  if (loading) return <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>Carregando…</div>;

  return (
    <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
      <RespostasOperador analiseId={analiseId} />
      <h3 style={{ marginTop: 0 }}>Análise Comparativa: Manual vs Automático</h3>
      <p style={{ color: '#7f8c8d' }}>
        Complete o lado automático com base nas respostas do operador acima (ex.: quanto tempo o sistema levará).
        {!podeEditar && <> <em style={{ color: '#c0392b' }}>(Não editável neste estado.)</em></>}
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead style={{ background: '#f8f9fa' }}>
          <tr>
            <th style={{ padding: 10, textAlign: 'left', width: '20%' }}>Característica</th>
            <th style={{ padding: 10, textAlign: 'left', width: '37%' }}>Manual (atual)</th>
            <th style={{ padding: 10, textAlign: 'left', width: '37%' }}>Automático (proposto)</th>
            {podeEditar && <th style={{ padding: 10, width: 40 }}></th>}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={i} style={{ borderTop: '1px solid #ecf0f1' }}>
              <td style={{ padding: 6 }}>
                <input style={{ ...INPUT_CELL, fontWeight: 600 }} value={l.caracteristica}
                       readOnly={!podeEditar}
                       onChange={(e) => atualizar(i, 'caracteristica', e.target.value)} />
              </td>
              <td style={{ padding: 6 }}>
                <textarea style={INPUT_CELL} value={l.processo_manual}
                          readOnly={!podeEditar}
                          placeholder="Como é hoje, manualmente..."
                          onChange={(e) => atualizar(i, 'processo_manual', e.target.value)} />
              </td>
              <td style={{ padding: 6 }}>
                <textarea style={INPUT_CELL} value={l.processo_automatico}
                          readOnly={!podeEditar}
                          placeholder="Como será com a automação..."
                          onChange={(e) => atualizar(i, 'processo_automatico', e.target.value)} />
              </td>
              {podeEditar && (
                <td style={{ padding: 6, textAlign: 'center' }}>
                  <button onClick={() => remover(i)}
                          style={{ background: 'none', border: 'none', color: '#e74c3c',
                                   cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {podeEditar && (
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button onClick={adicionar}
                  style={{ background: '#ecf0f1', padding: '8px 16px', border: 'none',
                           borderRadius: 4, cursor: 'pointer' }}>
            + adicionar linha
          </button>
          <button onClick={salvar}
                  style={{ background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', padding: '8px 20px',
                           border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            Salvar comparativa
          </button>
        </div>
      )}
    </div>
  );
}

const INPUT_CELL = {
  width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4,
  fontSize: '0.85rem', fontFamily: 'inherit', minHeight: 38, resize: 'vertical',
};

function AprovadoresAba({ analiseId, aprovadores, aprovacoes, usuarios, podeEditar, recarregar }) {
  const [novos, setNovos] = useState([]);

  useEffect(() => {
    if (aprovadores.length && novos.length === 0) {
      setNovos(aprovadores.map((a) => ({
        usuario_id: a.usuario.id, papel_descricao: a.papel_descricao,
        obrigatorio: a.obrigatorio, ordem: a.ordem,
      })));
    }
  }, [aprovadores]);

  async function salvar() {
    try {
      await aprovacoesAPI.substituirAprovadores(analiseId, novos);
      toast.success('Aprovadores salvos');
      await recarregar();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Erro ao salvar aprovadores');
    }
  }

  return (
    <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
      <h3>Configuração de aprovadores</h3>
      {podeEditar ? (
        <>
          {novos.map((n, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select value={n.usuario_id}
                      onChange={(e) => {
                        const x = [...novos];
                        x[i].usuario_id = parseInt(e.target.value);
                        setNovos(x);
                      }}
                      style={{ padding: 8, flex: 2 }}>
                {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome_completo}</option>)}
              </select>
              <input value={n.papel_descricao}
                     placeholder="Papel (ex: Gerente GNI)"
                     onChange={(e) => {
                       const x = [...novos];
                       x[i].papel_descricao = e.target.value;
                       setNovos(x);
                     }}
                     style={{ padding: 8, flex: 2 }} />
              <label><input type="checkbox" checked={n.obrigatorio}
                            onChange={(e) => {
                              const x = [...novos];
                              x[i].obrigatorio = e.target.checked;
                              setNovos(x);
                            }} /> obrig.</label>
              <button onClick={() => setNovos(novos.filter((_, j) => j !== i))}>remover</button>
            </div>
          ))}
          <button onClick={() => setNovos([...novos, { usuario_id: '', papel_descricao: '', obrigatorio: true, ordem: novos.length }])}
                  style={{ marginTop: 8 }}>+ adicionar aprovador</button>
          <br />
          <button onClick={salvar}
                  style={{ background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', padding: '10px 24px',
                           border: 'none', borderRadius: 4, marginTop: 16, cursor: 'pointer' }}>
            Salvar lista
          </button>
        </>
      ) : (
        <p style={{ color: '#999' }}><em>Lista não pode mais ser editada neste estado.</em></p>
      )}

      {aprovacoes.length > 0 && (
        <>
          <h3 style={{ marginTop: 24 }}>Aprovações em andamento</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f7f7f7' }}>
              <tr>
                <th style={{ padding: 8, textAlign: 'left' }}>Aprovador</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Papel</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Estado</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Lembretes</th>
                <th style={{ padding: 8, textAlign: 'left' }}>Respondido</th>
              </tr>
            </thead>
            <tbody>
              {aprovacoes.map((a) => (
                <tr key={a.id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 8 }}>{a.aprovador?.nome}</td>
                  <td style={{ padding: 8 }}>{a.papel_descricao}</td>
                  <td style={{ padding: 8 }}>
                    <span style={BADGE({ pendente: '#f39c12', aprovada: '#27ae60',
                                         rejeitada: '#e74c3c', expirada: '#95a5a6' }[a.estado])}>
                      {a.estado}
                    </span>
                  </td>
                  <td style={{ padding: 8 }}>{a.lembretes_enviados}</td>
                  <td style={{ padding: 8 }}>{a.respondido_em ? new Date(a.respondido_em).toLocaleString('pt-BR') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function HistoricoAba({ analiseId }) {
  const [eventos, setEventos] = useState([]);
  useEffect(() => {
    import('../../services/inovacaoAPI').then(({ adminInovacaoAPI }) =>
      adminInovacaoAPI.eventosDaEntidade('analise', analiseId).then(setEventos).catch(() => {})
    );
  }, [analiseId]);
  return (
    <div style={{ background: '#fff', padding: 24, borderRadius: 8 }}>
      <h3>Linha do tempo</h3>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {eventos.map((e) => (
          <li key={e.id} style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <code style={{ color: 'var(--brand, #20a490)' }}>{e.tipo}</code>{' '}
            <span style={{ color: '#777', fontSize: '0.85rem' }}>
              {new Date(e.ocorrido_em).toLocaleString('pt-BR')}
              {e.ator && ` · por ${e.ator.nome}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
